import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { ApiGet, ApiPost } from './Api';

const TEST_DOMAIN = 'raynertrust.co.uk';

const PHASE_LABELS = {
  starting: 'Starting',
  enabling: 'Enabling',
  enabled: 'Enabled temporarily',
  waiting: 'Enabled temporarily',
  disabling: 'Disabling',
  complete: 'Inactive',
  failed: 'Workflow failed'
};

const MANAGED_SERVICES = [
  {
    key: 'elementor-manage',
    title: 'Elementor Manage',
    description: `Temporarily enable Elementor Manage on ${TEST_DOMAIN}.`,
    apiPath: 'plugin-window',
    scope: 'single',
    enableOperation: 'enable-window',
    disableOperation: 'disable',
    enableLabel: 'Enable for 30 minutes',
    disableLabel: 'Disable now',
    enableConfirmation: `Enable Elementor Manage on ${TEST_DOMAIN} for 30 minutes? It will be disabled automatically.`,
    disableConfirmation: `Disable Elementor Manage on ${TEST_DOMAIN} now?`
  }
];

const formatDate = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getErrorMessage = error => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return String(error);
};

const ManagedService = ({ service }) => {
  const [loading, setLoading] = useState(true);
  const [pendingOperation, setPendingOperation] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [disableAt, setDisableAt] = useState(null);
  const [domain, setDomain] = useState(TEST_DOMAIN);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const result = await ApiGet(service.apiPath);

      setRunning(result?.running === true);
      setPhase(result?.phase ?? null);
      setStartedAt(result?.startedAt ?? null);
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.domain ?? TEST_DOMAIN);
      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [service.apiPath]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const interval = running && phase !== 'waiting' ? 5000 : 30000;
    const timer = window.setInterval(loadStatus, interval);

    return () => window.clearInterval(timer);
  }, [running, phase, loadStatus]);

  const startOperation = async operation => {
    setPendingOperation(operation);
    setError('');

    try {
      const result = await ApiPost(service.apiPath, {
        operation,
        scope: service.scope
      });

      setRunning(true);
      setPhase(operation === service.enableOperation ? 'starting' : 'disabling');
      setStartedAt(result?.startedAt ?? new Date().toISOString());
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.domain ?? TEST_DOMAIN);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      await loadStatus();
    } finally {
      setPendingOperation(null);
    }
  };

  const enable = () => {
    if (window.confirm(service.enableConfirmation)) {
      startOperation(service.enableOperation);
    }
  };

  const disable = () => {
    if (window.confirm(service.disableConfirmation)) {
      startOperation(service.disableOperation);
    }
  };

  const disableBlocked =
    loading ||
    pendingOperation !== null ||
    (
      running &&
      ['starting', 'enabling', 'disabling'].includes(phase)
    );

  const renderStatus = () => {
    if (loading) {
      return (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      );
    }

    if (!running) {
      return (
        <Alert variant="secondary">
          <strong>{service.title} is inactive.</strong>
          <div>No temporary-access workflow is currently running.</div>
          <div>Target domain: {domain}</div>
        </Alert>
      );
    }

    return (
      <Alert variant={phase === 'failed' ? 'danger' : 'warning'}>
        <div>
          <strong>
            {service.title}: {PHASE_LABELS[phase] ?? 'Workflow running'}
          </strong>
        </div>

        <div>Target domain: {domain}</div>

        {startedAt && <div>Started: {formatDate(startedAt)}</div>}
        {disableAt && <div>Automatic disable: {formatDate(disableAt)}</div>}

        {!disableAt && phase !== 'disabling' && (
          <div>Automatic disable is scheduled by the workflow.</div>
        )}
      </Alert>
    );
  };

  return (
    <>
      <Row>
        <Col>
          <h2 className="text-center">{service.title}</h2>
          <p className="text-center">{service.description}</p>
        </Col>
      </Row>

      <Row>
        <Col>
          {error && (
            <Alert variant="danger">
              <strong>Unable to complete the request.</strong>
              <div>{error}</div>
            </Alert>
          )}

          {renderStatus()}
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="text-center">
            <Button
              variant="primary"
              disabled={loading || pendingOperation !== null || running}
              onClick={enable}
            >
              {pendingOperation === service.enableOperation ? 'Enabling…' : service.enableLabel}
            </Button>

            {' '}

            <Button
              variant="danger"
              disabled={disableBlocked}
              onClick={disable}
            >
              {pendingOperation === service.disableOperation ? 'Disabling…' : service.disableLabel}
            </Button>

            {' '}

            <Button
              variant="outline-secondary"
              disabled={loading || pendingOperation !== null}
              onClick={loadStatus}
            >
              Refresh status
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

const Manage = () => {
  return (
    <Container fluid="lg">
      <Row>
        <Col>
          <h1 className="text-center">Manage</h1>
        </Col>
      </Row>

      {MANAGED_SERVICES.map(service => (
        <ManagedService key={service.key} service={service} />
      ))}
    </Container>
  );
};

export { Manage };